// Irrigation API wrappers.

import type {
  IrrigationPrescription,
  IrrigationStation,
  WaterBudget,
} from '@greeniq/shared';
import { api } from './client.js';

export interface PrescriptionsResponse {
  forDate: string;
  prescriptions: IrrigationPrescription[];
  waterBudget: WaterBudget | null;
}

export function getPrescriptions(courseSlug: string, forDate?: string): Promise<PrescriptionsResponse> {
  const qs = forDate ? `?forDate=${forDate}` : '';
  return api.get<PrescriptionsResponse>(`/courses/${courseSlug}/irrigation/prescriptions${qs}`);
}

export function getStations(courseSlug: string): Promise<{ stations: IrrigationStation[] }> {
  return api.get<{ stations: IrrigationStation[] }>(`/courses/${courseSlug}/irrigation/stations`);
}

export function seedDemoStations(courseSlug: string): Promise<{
  inserted: number;
  existing: number;
  zonesTouched: number;
}> {
  return api.post(`/courses/${courseSlug}/seed-demo-stations`);
}

export function generateIrrigationRx(courseSlug: string, forDate?: string): Promise<{ forDate: string; stations: number }> {
  const qs = forDate ? `?forDate=${forDate}` : '';
  return api.post(`/courses/${courseSlug}/generate-irrigation-rx${qs}`);
}

export interface IrrigationAlert {
  id: number;
  courseId: number;
  stationId: number;
  stationNumber: string | null;
  zoneId: number | null;
  zoneName: string | null;
  alertType: string;
  severity: 'info' | 'warn' | 'critical';
  forDate: string;
  observedMinutes: number | string;
  baselineMinutes: number | string;
  detail: Record<string, unknown> | null;
  status: 'unacknowledged' | 'acknowledged' | 'dismissed';
  createdAt: string;
}

export function getIrrigationAlerts(courseSlug: string): Promise<{ alerts: IrrigationAlert[] }> {
  return api.get(`/courses/${courseSlug}/irrigation/alerts`);
}

export function acknowledgeIrrigationAlert(courseSlug: string, alertId: number): Promise<{ alert: IrrigationAlert }> {
  return api.post(`/courses/${courseSlug}/irrigation/alerts/${alertId}/acknowledge`);
}

export interface HeatmapResponse {
  stations: Array<{ id: number; stationNumber: string; zoneName: string | null }>;
  dates: string[];
  cells: Array<{ stationId: number; date: string; runtimeMinutes: number }>;
}

export function getRuntimeHeatmap(courseSlug: string, days = 30): Promise<HeatmapResponse> {
  return api.get(`/courses/${courseSlug}/irrigation/heatmap?days=${days}`);
}
